import { CardTypes, MatchingPattern } from "../types/card-number.types";
import { cardDefaults } from "../data/card-number-formats";

/**
 * Read all prefixes and flatten them into an array.
 * Remove the non-numerical from value.
 * Check if the prefixes start with the value. If prefix starts, return the key
 * @param cardTypes
 * @param value
 */
export const findMatchingCreditCard = (
  cardTypes: CardTypes,
  value: string
): string | undefined => {
  // Remove the non-numerical from value.
  const valueStr = value
    ?.split("")
    .filter((i) => Number(i) === 0 || Number(i))
    .join("");
  const valueNum = parseInt(valueStr);

  const matchingCardTypes = Object.keys(cardTypes)
    .map((key): string | undefined => {
      // If prefix is a number, convert it into an array
      const prefix = cardTypes[key].prefix;
      const prefixes = Array.isArray(prefix) ? prefix : [prefix];

      // prefix can be nested array, need to flatten it
      const prefixesFlattened = prefixes
        .map((prefixItem: number[] | number): string | undefined => {
          // If range array found, check if value num is within range
          if (Array.isArray(prefixItem) && prefixItem.length === 2) {
            const [startRange, endRange] = prefixItem;

            // Compute the length of the prefix and use the length to get substring from input value.
            const prefixLength = startRange.toString().length;
            const valueTrimmed = parseInt(
              valueNum.toString().substring(0, prefixLength)
            );

            if (valueTrimmed >= startRange && valueTrimmed <= endRange) {
              return valueStr;
            }
          }
          return prefixItem.toString();
        })
        .filter((i) => i)
        .join(",")
        .split(",");

      // Check if value starts with prefix value and return matching key
      const matchingPrefix = prefixesFlattened.find(
        (prefix: string): string | undefined =>
          prefix === valueStr.substring(0, prefix.length) ? key : undefined
      );

      if (matchingPrefix) {
        return key;
      }

      return undefined;
    })
    .filter((i) => i);

  return matchingCardTypes?.[matchingCardTypes.length - 1];
};

/**
 * Let's say the value in card input is of length 19 (16 numbers + 3 padding)
 * The input value length matches with 16 and it should have provision to enter more numbers
 * The type format needs to have matching pattern along with placeholders for rest
 * @param bestMatch
 * @param nextMatch
 * @param fillStr
 */
const generateTypeFormatForOneOrMoreMatchingPatterns = (
  bestMatch: number[],
  nextMatch: number[],
  fillStr: string,
  valueLength = 0,
  isRegex = false
): string => {
  const bestMatchType = generateTypeFormat(bestMatch, fillStr);
  const nextMatchType = generateTypeFormat(nextMatch, fillStr);
  const bestMatchSum =
    bestMatch.reduce((item, sum) => item + sum, 0) + bestMatch.length - 1;
  const nextPieces = generateTypeFormat(
    Array(1).fill((nextMatchType.length - bestMatchType.length) / 2),
    fillStr
  );

  if (!isRegex) {
    if (valueLength < bestMatchSum) {
      return bestMatchType;
    }
    return bestMatchType + nextPieces;
  }

  if (valueLength < bestMatchSum) {
    return bestMatchType;
  } else if (valueLength === bestMatchSum) {
    return bestMatchType + "\\s?";
  } else {
    return "(\\d\\s?){" + nextMatch.reduce((item, sum) => item + sum, 0) + "}";
  }
};

/**
 * Pass in the pattern and the method returns the string matching
 * the pattern filled with the fillStr
 */
const generateTypeFormat = (pattern: number[], fillStr: string): string =>
  pattern
    .map((p) => {
      let format = "";
      for (let i = 0; i < p; i++) {
        format = format + fillStr;
      }
      return format;
    })
    .join(" ");

/**
 * Identify the matching card type based on the input value & return the meta data
 * related to the input
 */
export const getMatchingMetaData = (
  cardTypes: CardTypes,
  key?: string,
  value?: string
): MatchingPattern => {
  const card = key ? cardTypes[key] : undefined;

  // If no card or value is found, then use the default metadata
  if (!card || !value) {
    const staticPattern = [4, 4, 4, 4];
    const staticPatternLength = 16;
    return {
      placeholder: generateTypeFormat(staticPattern, "0"),
      pattern: generateTypeFormat(staticPattern, "\\d"),
      patternRegex: generateTypeFormat(staticPattern, "\\d"),
      minLength: staticPatternLength,
      maxLength: staticPatternLength + staticPattern.length - 1,
    };
  }

  const isPatternAnNestedArray: boolean =
    Array.isArray(card.pattern) &&
    Array.isArray(card.pattern[0]) &&
    card.pattern[0].length > 0;

  // We are normalising patterns to a nested array
  const patterns = isPatternAnNestedArray ? card.pattern : [card.pattern];

  // We are normalising cardLengths to an array
  const cardNumberLengths = Array.isArray(card.length)
    ? card.length
    : [card.length];

  const hasSpacesInValue = value.includes(" ");

  // Compute the length of value entered
  const valueLength = hasSpacesInValue ? value.length : value.length + 3;

  let nextPermissibleCardLength = cardNumberLengths[0];

  cardNumberLengths.forEach((cardNumberLength, index) => {
    const patternWithFillerLength = (patterns[index] as number[][]).length - 1;

    // find if valueLength is greater than some card length,
    // if not return the first card length
    if (valueLength > cardNumberLength + patternWithFillerLength) {
      // If index is less than cardNumberLengths, then next length should be permitted
      if (index < cardNumberLengths.length - 1) {
        nextPermissibleCardLength = cardNumberLengths[index + 1];
      } else {
        nextPermissibleCardLength = cardNumberLengths[index];
      }
    }
  });

  const matchingCardNumberIndex = cardNumberLengths.indexOf(
    nextPermissibleCardLength
  );

  const minLength =
    cardNumberLengths[matchingCardNumberIndex] +
    (patterns[matchingCardNumberIndex] as number[]).length -
    1;

  const maxLength =
    cardNumberLengths[cardNumberLengths.length - 1] +
    (patterns[patterns.length - 1] as number[]).length -
    1;

  const nextPatternIndex =
    matchingCardNumberIndex + 1 >= cardNumberLengths.length
      ? matchingCardNumberIndex
      : matchingCardNumberIndex + 1;

  return {
    placeholder: generateTypeFormat(
      patterns[matchingCardNumberIndex] as number[],
      "0"
    ),
    pattern: generateTypeFormatForOneOrMoreMatchingPatterns(
      patterns[matchingCardNumberIndex] as number[],
      patterns[nextPatternIndex] as number[],
      "\\d",
      valueLength
    ),
    patternRegex: generateTypeFormatForOneOrMoreMatchingPatterns(
      patterns[matchingCardNumberIndex] as number[],
      patterns[nextPatternIndex] as number[],
      "\\d",
      valueLength,
      true
    ),
    minLength,
    maxLength,
  };
};

/**
 * Check if defaultCardsDisabled is true, return only custom card data.
 */
export const getCardTypes = (
  defaultCardsDisabled: boolean,
  customCardTypes?: CardTypes,
  disableCardDefaults: string[] = []
): CardTypes | null => {
  if (defaultCardsDisabled) {
    if (!customCardTypes) {
      console.warn(
        "At least one card type has to be provided using the cardTypes prop for <CardNumber />"
      );
      return null;
    }

    return customCardTypes;
  }

  const copyOfCardDefaults = { ...cardDefaults };

  disableCardDefaults.forEach((cardToBeRemoved) => {
    // @ts-ignore
    delete copyOfCardDefaults[cardToBeRemoved];
  });

  return Object.assign({}, copyOfCardDefaults, customCardTypes);
};


/**
 * Validate the value using the Luhn algorithm, tests for valid credit card number combination
 * @returns boolean
 */
export const runLuhnTest = (value: string): boolean => {
  if (value === "") {
    return false;
  }

  let nCheck = 0;
  let nDigit = 0;
  let bEven = false;
  value = value.replace(/\D/g, "");

  for (let n = value.length - 1; n >= 0; n--) {
    const cDigit = value.charAt(n);
    nDigit = parseInt(cDigit, 10);

    if (bEven) {
      if ((nDigit *= 2) > 9) nDigit -= 9;
    }

    nCheck += nDigit;
    bEven = !bEven;
  }

  return nCheck % 10 === 0;
}
